import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { redeemInviteCode } from '../services/betaSignUp';
import { subscribeToAccountStatus } from '../services/accountStatus';
import { ACCOUNT_PAUSED_MESSAGE, isAccountPausedError } from '../utils/accountStatus';

const SESSION_TIMEOUT_KEY = 'votebeats_session_timeout';
const LISTENING_TIMEOUT_KEY = 'votebeats_listening_timeout';
const ACTIVE_PLAYBACK_KEY = 'votebeats_active_playback';
const DEFAULT_SESSION_TIMEOUT = 60; // Default 1 hour in minutes
const DEFAULT_LISTENING_TIMEOUT = 240; // Default 4 hours in minutes (0 = never)

export type AdminTier = 'owner' | 'support';

interface AuthClaims {
  isDJ: boolean;
  adminTier: AdminTier | null;
}

interface AuthContextType {
  user: User | null;
  isDJ: boolean;
  adminTier: AdminTier | null;
  loading: boolean;
  authNotice: string | null;
  clearAuthNotice: () => void;
  // undefined = not loaded yet (or the users doc couldn't be read); null = never accepted (ITER-155)
  termsAcceptedVersion: string | null | undefined;
  termsLoadFailed: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, displayName: string, inviteCode: string, termsVersion: string) => Promise<void>;
  refreshClaims: () => Promise<boolean>;
  resendVerificationEmail: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sessionTimeoutMinutes: number;
  setSessionTimeoutMinutes: (minutes: number) => void;
  listeningModeTimeoutMinutes: number;
  setListeningModeTimeoutMinutes: (minutes: number) => void;
  updateActivity: () => void;
}

function getStoredSessionTimeout(): number {
  const stored = localStorage.getItem(SESSION_TIMEOUT_KEY);
  if (stored) {
    const timeout = parseInt(stored, 10);
    if (!isNaN(timeout) && timeout > 0) {
      return timeout;
    }
  }
  return DEFAULT_SESSION_TIMEOUT;
}

function getStoredListeningTimeout(): number {
  const stored = localStorage.getItem(LISTENING_TIMEOUT_KEY);
  if (stored) {
    const timeout = parseInt(stored, 10);
    if (!isNaN(timeout) && timeout >= 0) {
      return timeout;
    }
  }
  return DEFAULT_LISTENING_TIMEOUT;
}

const NO_CLAIMS: AuthClaims = { isDJ: false, adminTier: null };

function parseAdminTier(value: unknown): AdminTier | null {
  return value === 'owner' || value === 'support' ? value : null;
}

async function readClaims(firebaseUser: User, forceRefresh = false): Promise<AuthClaims> {
  if (firebaseUser.isAnonymous) return NO_CLAIMS;
  try {
    let tokenResult = await firebaseUser.getIdTokenResult(forceRefresh);
    if (tokenResult.claims.dj !== true && !forceRefresh) {
      // Force one refresh so a DJ granted the claim after their last sign-in does not wait an hour
      tokenResult = await firebaseUser.getIdTokenResult(true);
    }
    return { isDJ: tokenResult.claims.dj === true, adminTier: parseAdminTier(tokenResult.claims.admin) };
  } catch (error) {
    console.error('Failed to read auth claims', error);
    return NO_CLAIMS;
  }
}

function verificationSettings() {
  return { url: `${window.location.origin}/dashboard` };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isDJ, setIsDJ] = useState(false);
  const [adminTier, setAdminTier] = useState<AdminTier | null>(null);
  const [loading, setLoading] = useState(true);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [termsAcceptedVersion, setTermsAcceptedVersion] = useState<string | null | undefined>(undefined);
  const [termsLoadFailed, setTermsLoadFailed] = useState(false);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutesState] = useState(getStoredSessionTimeout);
  const [listeningModeTimeoutMinutes, setListeningModeTimeoutMinutesState] = useState(getStoredListeningTimeout);
  const lastActivityRef = useRef<number>(Date.now());
  const sessionCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const setSessionTimeoutMinutes = useCallback((minutes: number) => {
    setSessionTimeoutMinutesState(minutes);
    localStorage.setItem(SESSION_TIMEOUT_KEY, minutes.toString());
  }, []);

  const setListeningModeTimeoutMinutes = useCallback((minutes: number) => {
    setListeningModeTimeoutMinutesState(minutes);
    localStorage.setItem(LISTENING_TIMEOUT_KEY, minutes.toString());
  }, []);

  // Check session expiry based on inactivity
  const checkSessionExpiry = useCallback(() => {
    if (!user) return;

    // If Spotify listening mode is active, use the listening mode timeout
    const isListening = !!localStorage.getItem(ACTIVE_PLAYBACK_KEY);
    const effectiveTimeout = isListening ? listeningModeTimeoutMinutes : sessionTimeoutMinutes;

    // 0 means "never timeout" while listening
    if (effectiveTimeout === 0) return;

    const timeoutMs = effectiveTimeout * 60 * 1000;
    const now = Date.now();
    if (now - lastActivityRef.current > timeoutMs) {
      console.log(`Session expired due to inactivity (${isListening ? 'listening mode' : 'standard'} timeout)`);
      signOut(auth);
    }
  }, [user, sessionTimeoutMinutes, listeningModeTimeoutMinutes]);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Resolve the claim before clearing loading so ProtectedRoute never sees a DJ as a non-DJ
      const claims = firebaseUser ? await readClaims(firebaseUser) : NO_CLAIMS;
      if (firebaseUser) {
        lastActivityRef.current = Date.now();
      }
      setUser(firebaseUser);
      setIsDJ(claims.isDJ);
      setAdminTier(claims.adminTier);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // A paused account (ITER-105) is signed out at once, instead of when its ID token expires.
  // The same listener reads the accepted Terms version for the acceptance gate (ITER-155)
  const pauseWatchUid = user && !user.isAnonymous ? user.uid : null;
  useEffect(() => {
    setTermsAcceptedVersion(undefined);
    setTermsLoadFailed(false);
    if (!pauseWatchUid) return;
    return subscribeToAccountStatus(
      pauseWatchUid,
      (status) => {
        setTermsAcceptedVersion(status.termsAcceptedVersion);
        setTermsLoadFailed(false);
        if (!status.accountPaused) return;
        setAuthNotice(ACCOUNT_PAUSED_MESSAGE);
        signOut(auth).catch((error: unknown) => console.error('Failed to sign out a paused account', error));
      },
      (error) => {
        console.error('Failed to watch the account pause flag', error);
        setTermsAcceptedVersion(undefined);
        setTermsLoadFailed(true);
      }
    );
  }, [pauseWatchUid]);

  const clearAuthNotice = useCallback(() => setAuthNotice(null), []);

  // Set up periodic session check and activity listeners
  useEffect(() => {
    if (user) {
      sessionCheckIntervalRef.current = setInterval(checkSessionExpiry, 60000);

      const handleActivity = () => {
        if (activityTimeoutRef.current) return;
        activityTimeoutRef.current = setTimeout(() => {
          updateActivity();
          activityTimeoutRef.current = null;
        }, 1000);
      };

      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('click', handleActivity);
      window.addEventListener('scroll', handleActivity);

      return () => {
        if (sessionCheckIntervalRef.current) {
          clearInterval(sessionCheckIntervalRef.current);
        }
        if (activityTimeoutRef.current) {
          clearTimeout(activityTimeoutRef.current);
        }
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        window.removeEventListener('click', handleActivity);
        window.removeEventListener('scroll', handleActivity);
      };
    }
  }, [user, checkSessionExpiry, updateActivity]);

  const login = async (email: string, password: string): Promise<boolean> => {
    let credential;
    try {
      credential = await signInWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      if (isAccountPausedError(error)) throw new Error(ACCOUNT_PAUSED_MESSAGE);
      throw error;
    }
    // Settle DJ status before the caller navigates into a protected route
    const claims = await readClaims(credential.user);
    setUser(credential.user);
    setIsDJ(claims.isDJ);
    setAdminTier(claims.adminTier);
    return claims.isDJ;
  };

  const register = async (email: string, password: string, displayName: string, inviteCode: string, termsVersion: string) => {
    await redeemInviteCode({ code: inviteCode, email, password, displayName, termsVersion });
    const credential = await signInWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(credential.user, verificationSettings());
    setUser(credential.user);
    setIsDJ(false);
    setAdminTier(null);
  };

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) throw new Error('No signed-in account to verify.');
    await sendEmailVerification(auth.currentUser, verificationSettings());
  };

  const refreshClaims = async (): Promise<boolean> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    await currentUser.reload();
    const claims = await readClaims(currentUser, true);
    setIsDJ(claims.isDJ);
    setAdminTier(claims.adminTier);
    return claims.isDJ;
  };

  const logout = async () => {
    await signOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const value = {
    user,
    isDJ,
    adminTier,
    loading,
    authNotice,
    clearAuthNotice,
    termsAcceptedVersion,
    termsLoadFailed,
    login,
    register,
    refreshClaims,
    resendVerificationEmail,
    logout,
    resetPassword,
    sessionTimeoutMinutes,
    setSessionTimeoutMinutes,
    listeningModeTimeoutMinutes,
    setListeningModeTimeoutMinutes,
    updateActivity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
